/**
 * Reload to a new build when one is deployed, at a moment that cannot
 * interrupt the fan.
 *
 * Why: every push deploys immediately, but a tab or home-screen app that
 * was already open keeps running the old code until it is closed - fans
 * kept "still seeing" a layout that had been fixed for hours. index.html
 * is served with max-age=0, so fetching it again is cheap and always
 * fresh; its script tag names the current bundle (index-<hash>.js). If
 * that differs from the bundle this page loaded, a new build exists.
 *
 * When it reloads: only when the tab becomes visible again after being
 * hidden (switching back to the app is a natural break), or on a 10-minute
 * timer while visible - and never while the fan is typing in a field.
 */
import { useEffect } from 'react'

const CHECK_EVERY_MS = 10 * 60 * 1000

function currentBundle(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return s ? s.src.replace(/^.*\//, '') : null
}

async function latestBundle(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const html = await res.text()
    const m = html.match(/\/assets\/(index-[A-Za-z0-9_-]+\.js)/)
    return m ? m[1] : null
  } catch {
    return null
  }
}

function fanIsTyping(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable
}

export function useReloadOnNewBuild() {
  useEffect(() => {
    const running = currentBundle()
    if (!running || import.meta.env.DEV) return

    let checking = false
    const check = async () => {
      if (checking || document.visibilityState !== 'visible' || fanIsTyping()) return
      checking = true
      const latest = await latestBundle()
      checking = false
      if (latest && latest !== running) window.location.reload()
    }

    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(check, CHECK_EVERY_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [])
}
